import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { signOut } from "../utils/signOut";

const TRIGGER_SELECTOR = "[data-settings-menu-trigger]";

/**
 * Small account-style menu: Settings (opens modal) + Sign out.
 * — &lt; nav (550px): fixed top-right under mobile header
 * — nav+: fixed bottom-left (near sidebar gear), like a bottom-anchored menu
 */
const SettingsMenu = ({ open, onClose, onOpenSettingsModal, onOpenFeedbackModal }) => {
  const navigate = useNavigate();
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (e) => {
      if (panelRef.current?.contains(e.target)) return;
      if (e.target.closest(TRIGGER_SELECTOR)) return;
      onClose();
    };

    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  const handleSettings = () => {
    onOpenSettingsModal();
    onClose();
  };

  const handleGiveFeedback = () => {
    onOpenFeedbackModal();
    onClose();
  };

  const handleSignOut = () => {
    onClose();
    signOut(navigate);
  };

  return (
    <>
      {/* Dim overlay on small screens so tap-outside is obvious */}
      <div
        className="fixed inset-0 z-[60] bg-black/20 nav:hidden"
        aria-hidden
        onClick={onClose}
      />

      <div
        ref={panelRef}
        role="menu"
        aria-label="Account menu"
        className="fixed z-[70] w-[min(17.5rem,calc(100vw-1.5rem))] overflow-hidden rounded-2xl border border-gray-200 bg-white py-1.5 shadow-xl ring-1 ring-black/5 top-16 right-3 nav:top-auto nav:left-4 nav:right-auto nav:bottom-4"
      >
        <button
          type="button"
          role="menuitem"
          onClick={handleSettings}
          className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm font-medium text-[#202531] transition-colors hover:bg-[#0196BF]/10 hover:text-[#0196BF]"
        >
          <span className="material-icons text-xl text-gray-500" aria-hidden>
            settings
          </span>
          Settings
        </button>

        <button
          type="button"
          role="menuitem"
          onClick={handleGiveFeedback}
          className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm font-medium text-[#202531] transition-colors hover:bg-[#0196BF]/10 hover:text-[#0196BF]"
        >
          <span className="material-icons text-xl text-gray-500" aria-hidden>
            feedback
          </span>
          Give feedback
        </button>

        <div className="my-1 border-t border-gray-100" />

        <button
          type="button"
          role="menuitem"
          onClick={handleSignOut}
          className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm font-medium text-[#202531] transition-colors hover:bg-red-50 hover:text-red-700"
        >
          <span className="material-icons text-xl text-gray-500" aria-hidden>
            logout
          </span>
          Sign out
        </button>
      </div>
    </>
  );
};

export default SettingsMenu;
